import { makeStyles } from '@material-ui/core/styles';
import { CART_DRAWER_WIDTH, CART_BREAKPOINT, APP_FOOTER_HEIGHT, APP_FOOTER_HEIGHT_XS } from './cartLayout';

const useStyles = makeStyles((theme) => ({
    drawer: {
      width: CART_DRAWER_WIDTH,
      flexShrink: 0,
    },
    drawerPaper: {
      width: CART_DRAWER_WIDTH,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      backgroundColor: "#ffffff",
      borderLeft: "1px solid #e2e8f0",
      boxShadow: "-12px 0 30px rgba(15, 23, 42, 0.08)",
      // Stay at the top of the viewport so this drawer covers the app Header
      // on the right (POS already has AppBarComp). Only leave the global
      // Footer visible underneath.
      height: `calc(100vh - ${APP_FOOTER_HEIGHT}px)`,
      [theme.breakpoints.down("xs")]: {
        width: "100vw",
        maxWidth: "100vw",
        height: `calc(100vh - ${APP_FOOTER_HEIGHT_XS}px)`,
      },
      [theme.breakpoints.between("sm", CART_BREAKPOINT)]: {
        width: "min(580px, 90vw)",
        maxWidth: "90vw",
      },
    },
    drawerHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
      flexWrap: "wrap",
      gap: 4,
      minHeight: 56,
      padding: theme.spacing(0.5, 1),
      backgroundColor: "#ffffff",
      borderBottom: "1px solid #f1f5f9",
      "& .MuiIconButton-root": {
        width: 36,
        height: 36,
        margin: 0,
        borderRadius: 8,
        transition: "all 0.15s ease",
        "&:hover": {
          backgroundColor: "#f8fafc",
          transform: "translateY(-1px)",
        },
      },
      [theme.breakpoints.down("xs")]: {
        padding: theme.spacing(0.5, 0.5),
        "& .MuiIconButton-root": {
          width: 32,
          height: 32,
        },
        "& .MuiIconButton-root .MuiSvgIcon-root": {
          fontSize: "1.1rem",
        },
        "& .MuiAvatar-root": {
          width: 30,
          height: 30,
          fontSize: "0.85rem",
        },
      },
    },
    headerIconButton: {
      width: 36,
      height: 36,
      borderRadius: 8,
      padding: 6,
      color: "#475569",
      transition: "all 0.15s ease",
      "&:hover": {
        backgroundColor: "#fff7ed",
        color: "#ea580c",
        transform: "translateY(-1px)",
      },
    },
    tableWrapper: {
      flex: "1 1 auto",
      minHeight: 0,
      margin: theme.spacing(1, 1.25),
      overflowY: "auto",
      overflowX: "auto",
      border: "1px solid #e2e8f0",
      borderRadius: 12,
      backgroundColor: "#ffffff",
      boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
      "&::-webkit-scrollbar": {
        width: "5px",
        height: "5px",
      },
      "&::-webkit-scrollbar-track": {
        background: "transparent",
      },
      "&::-webkit-scrollbar-thumb": {
        background: "rgba(148, 163, 184, 0.4)",
        borderRadius: "4px",
        "&:hover": {
          background: "rgba(100, 116, 139, 0.7)",
        },
      },
      scrollbarWidth: "thin",
      scrollbarColor: "rgba(148, 163, 184, 0.4) transparent",
      [theme.breakpoints.up(CART_BREAKPOINT)]: {
        overflowX: "hidden",
      },
    },
    table: {
      width: "100%",
      minWidth: 0,
      tableLayout: "fixed",
      borderCollapse: "collapse",
      "& .MuiTableCell-root:nth-child(1)": {
        width: "32%",
      },
      "& .MuiTableCell-root:nth-child(2)": {
        width: "16%",
        textAlign: "center",
      },
      "& .MuiTableCell-root:nth-child(3)": {
        width: "20%",
        textAlign: "right",
      },
      "& .MuiTableCell-root:nth-child(4)": {
        width: "20%",
        textAlign: "right",
      },
      "& .MuiTableCell-root:nth-child(5)": {
        width: "12%",
        textAlign: "center",
        padding: "6px 2px",
      },
    },
    tableRow: {
      transition: "background-color 0.15s ease",
      "&:hover": {
        backgroundColor: "#f8fafc",
      },
    },
    tableHead: {
      position: "sticky",
      top: 0,
      zIndex: 2,
      backgroundColor: "#f8fafc",
      color: "#475569",
      fontWeight: 700,
      fontSize: "0.72rem",
      textTransform: "uppercase",
      letterSpacing: "0.04em",
      whiteSpace: "nowrap",
      padding: theme.spacing(0.85, 0.75),
      borderBottom: "1px solid #e2e8f0",
    },
    tableCell: {
      padding: theme.spacing(0.85, 0.75),
      borderBottom: "1px solid #f1f5f9",
      textAlign: "left",
      fontSize: "0.8125rem",
      overflowWrap: "anywhere",
      "&:nth-child(3), &:nth-child(4)": {
        whiteSpace: "nowrap",
        overflowWrap: "normal",
      },
    },
    tableFooter: {
      fontWeight: "bold",
    },
    totalAmountSection: {
      width: "100%",
      padding: theme.spacing(1.25, 1.5),
      backgroundColor: "#ffffff",
      borderTop: "1px solid #e2e8f0",
      fontSize: "0.85rem",
    },
    totalAmountText: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: theme.spacing(1),
      marginBottom: theme.spacing(0.5),
      fontSize: "0.8125rem",
      color: "#475569",
      fontWeight: 500,
    },
    totalCard: {
      backgroundColor: "#f8fafc",
      border: "1px solid #e2e8f0",
      borderRadius: 10,
      padding: theme.spacing(1, 1.25),
      marginTop: theme.spacing(0.75),
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    totalLabel: {
      fontSize: "0.95rem",
      fontWeight: 800,
      color: "#0f172a",
      letterSpacing: "-0.01em",
    },
    totalValue: {
      fontSize: "1.25rem",
      fontWeight: 800,
      color: "#ea580c",
      fontVariantNumeric: "tabular-nums",
      letterSpacing: "-0.02em",
    },
    amountValue: {
      fontWeight: 600,
      color: "#0f172a",
      fontSize: "0.875rem",
      fontVariantNumeric: "tabular-nums",
    },
    emptyCart: {
      minHeight: 220,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: theme.spacing(1),
      color: "#64748b",
      textAlign: "center",
      padding: theme.spacing(3, 2),
    },
    emptyCartIconBox: {
      width: 60,
      height: 60,
      borderRadius: "50%",
      backgroundColor: "#fff7ed",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: theme.spacing(0.75),
    },
    emptyCartIcon: {
      fontSize: 30,
      color: "#ea580c",
    },
    emptyCartTitle: {
      fontSize: "0.9375rem",
      fontWeight: 700,
      color: "#1e293b",
      marginBottom: 2,
    },
    emptyCartSubtitle: {
      fontSize: "0.78rem",
      color: "#94a3b8",
      maxWidth: 240,
    },
    deleteIcon: {
      color: "#94a3b8",
      padding: 5,
      borderRadius: 6,
      transition: "all 0.15s ease",
      "&:hover": {
        color: "#dc2626",
        backgroundColor: "#fee2e2",
        transform: "scale(1.05)",
      },
    },
    qtyPill: {
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      padding: "3px 8px",
      borderRadius: 6,
      backgroundColor: "#f1f5f9",
      color: "#1e293b",
      fontWeight: 700,
      fontSize: "0.8125rem",
      border: "1px solid #e2e8f0",
      transition: "all 0.15s ease",
      cursor: "pointer",
      "&:hover": {
        backgroundColor: "#e2e8f0",
        borderColor: "#cbd5e1",
      },
    },
    holdActionBar: {
      padding: "8px 12px",
      display: "flex",
      gap: 8,
      flexWrap: "wrap",
      alignItems: "center",
      backgroundColor: "#f8fafc",
      borderBottom: "1px solid #e2e8f0",
    },
    holdBtn: {
      flex: 1,
      minWidth: 70,
      minHeight: 32,
      borderRadius: 8,
      textTransform: "none",
      fontWeight: 600,
      fontSize: "0.8125rem",
      borderColor: "#cbd5e1",
      color: "#334155",
      backgroundColor: "#ffffff",
      "&:hover": {
        backgroundColor: "#f1f5f9",
        borderColor: "#94a3b8",
      },
    },
    kotBtn: {
      flex: "1 1 auto",
      minHeight: 32,
      borderRadius: 8,
      textTransform: "none",
      fontWeight: 600,
      fontSize: "0.8125rem",
      backgroundColor: "#ea580c",
      backgroundImage: "linear-gradient(135deg, #ea580c 0%, #f97316 100%)",
      color: "#ffffff",
      boxShadow: "0 2px 6px rgba(234, 88, 12, 0.25)",
      "&:hover": {
        backgroundColor: "#c2410c",
        backgroundImage: "none",
      },
    },
    sessionBadge: {
      flex: "0 0 100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
      padding: "4px 8px",
      borderRadius: 6,
      backgroundColor: "#fff7ed",
      border: "1px solid #fed7aa",
    },
    payNowButton: {
      margin: theme.spacing(1, 1.25, 1.25),
      minHeight: 46,
      borderRadius: 12,
      backgroundColor: "#ef7f1a",
      backgroundImage: "linear-gradient(135deg, #ef7f1a 0%, #f97316 100%)",
      color: "#ffffff",
      fontSize: "0.9375rem",
      fontWeight: 700,
      textTransform: "none",
      letterSpacing: "0.01em",
      boxShadow: "0 6px 16px rgba(239, 127, 26, 0.28)",
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor: "#d96f17",
        backgroundImage: "linear-gradient(135deg, #ea580c 0%, #d96f17 100%)",
        boxShadow: "0 8px 20px rgba(239, 127, 26, 0.38)",
        transform: "translateY(-1px)",
      },
      "&:active": {
        transform: "translateY(0)",
      },
      "&.Mui-disabled": {
        backgroundColor: "#e2e8f0",
        backgroundImage: "none",
        color: "#94a3b8",
        boxShadow: "none",
        cursor: "not-allowed",
      },
    },
    incrementDecrementButton: {
      fontSize: "16px",
      fontWeight: "bold",
      padding: "4px",
      backgroundColor: "#ef7f1a",
      color: "#fff",
      "&:hover": {
        backgroundColor: "#d96f17",
      },
    },
    quantityText: {
      fontWeight: "bold",
      marginRight: theme.spacing(1),
    },
  
    dialog: {
      maxWidth: "360px",
      width: "100%",
      margin: theme.spacing(2),
      borderRadius: 16,
      overflow: "hidden",
      [theme.breakpoints.down("xs")]: {
        margin: theme.spacing(1.5),
        maxWidth: "calc(100% - 24px)",
      },
    },
    dialogTitle: {
      backgroundColor: theme.palette.primary.main,
      color: "#fff",
      fontWeight: 700,
      fontSize: "1.05rem",
      padding: theme.spacing(1.5, 2.5),
    },
    dialogContent: {
      padding: theme.spacing(2.5, 2),
    },
    itemText: {
      marginBottom: theme.spacing(0.25),
      fontWeight: 700,
      fontSize: "1.05rem",
      color: "#1f2d3d",
      textAlign: "center",
      lineHeight: 1.3,
    },
    unitBadge: {
      display: "inline-flex",
      alignItems: "center",
      padding: theme.spacing(0.25, 1.25),
      borderRadius: 20,
      backgroundColor: "rgba(239, 127, 26, 0.1)",
      color: theme.palette.primary.main,
      fontSize: "0.7rem",
      fontWeight: 700,
      letterSpacing: "0.02em",
      textTransform: "uppercase",
      marginBottom: theme.spacing(1.75),
    },
    pillContainer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexDirection: "column",
      width: "100%",
    },
    pillButtonGroup: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      gap: theme.spacing(1),
      backgroundColor: "#f8fafc",
      borderRadius: "60px",
      width: "100%",
      maxWidth: "14rem",
      padding: theme.spacing(0.5, 1),
      border: "1px solid #e8edf3",
      boxSizing: "border-box",
    },
    pillIconButton: {
      padding: "9px",
      color: "#fff",
      backgroundColor: theme.palette.primary.main,
      flexShrink: 0,
      transition: "background-color 0.15s ease, transform 0.1s ease",
      "&:hover": {
        backgroundColor: "#d96f17",
      },
      "&:active": {
        transform: "scale(0.94)",
      },
      "&.Mui-disabled": {
        backgroundColor: "#e2e8f0",
        color: "#94a3b8",
      },
    },
    modal: {
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: 400,
      backgroundColor: theme.palette.background.paper,
      boxShadow: theme.shadows[5],
      padding: theme.spacing(4),
      outline: "none",
    },
    AddButtonModal: {
      marginTop: theme.spacing(4),
    },
    modalButtons: {
      display: "flex",
      justifyContent: "space-between",
    },
    quantityInput: {
      width: '92px',
      minWidth: 0,
      flexShrink: 1,
      '& input': {
        padding: '8px 2px',
        fontSize: '1.3rem',
        fontWeight: 700,
        textAlign: 'center',
        color: '#1f2d3d',
      },
      '& .MuiOutlinedInput-root': {
        borderRadius: 10,
        backgroundColor: '#fff',
      },
      '& input[type=number]': {
        '-moz-appearance': 'textfield', // Remove arrows in Firefox
      },
      '& input::-webkit-outer-spin-button': {
        '-webkit-appearance': 'none', // Remove arrows in Chrome/Safari
        margin: 0,
      },
      '& input::-webkit-inner-spin-button': {
        '-webkit-appearance': 'none',
        margin: 0,
      },
    },

  }));
export default useStyles;
